import React, {useCallback, useEffect, useMemo, useState} from "react";
import useAccessLevels from "../../../../hooks/use-access-levels";
import DraggablePopover from "../index";
import {Box, IconButton, Tooltip, Typography} from "@mui/material";
import {CloseOutlined, ControlCamera, OpenInNew} from "@mui/icons-material";
import {ProductOnBackorderListDataGridColumnNames, ProductOnPurchaseListDataGridColumnNames} from "../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../../packages/data-grid";
import useIsMounted from "../../../../hooks/use-is-mounted";
import axios from "axios";
import PartnerDataGrid from "../../../../containers/data-grid";
import Api from "../../../../../core/services/api/api";
import Button from "../../../base/button/button";
import {routeFunctions} from "../../../../../core/models/static/routes/app-routes";
import CreateUserOnlineProfileDialog from "../../../dialogs/create-user-online-profile";
import {openLinkInNewTab} from "../../../../../core/services/utils/utils";

const ProductOnBackorderPopover = ({
                                       children,
                                       wrapperProps,
                                       buttonProps,
                                       iconProps,
                                       partNo,
                                       warehouseCode,
                                       cancelSelector,
                                       backOrderQty,
                                       customerId,
                                       customerName,
                                   }) => {
    const operatorAccessLevels = useAccessLevels();
    const canRenderPopover = useMemo(() =>
            operatorAccessLevels.packages.lookup.productOnBackorderList &&
            !!partNo &&
            !!warehouseCode &&
            backOrderQty > 0
        , [operatorAccessLevels.packages.lookup.productOnBackorderList, partNo, warehouseCode, backOrderQty]);

    return (
        <>
            <DraggablePopover
                popupId={`product-on-backorder-popover-${partNo}-${warehouseCode}`}
                canRenderPopover={canRenderPopover}
                wrapperProps={wrapperProps}
                buttonProps={buttonProps}
                iconProps={iconProps}
                popoverElement={
                    <ProductOnBackorderList
                        partNo={partNo}
                        warehouseCode={warehouseCode}
                        customerId={customerId}
                        customerName={customerName}
                    />}
                iconOnRight={false}
                width={800}
                height={600}
                draggableProps={{
                    cancel: `.draggable-popover-cancel ${cancelSelector ? `, ${cancelSelector}` : ""}`
                }}
                popperProps={{
                    slotProps: {
                        root: {
                            className: "product-on-backorder-popover-class",
                        },
                    },
                }}
            >
                {children}
            </DraggablePopover>
        </>
    );
}

const initNewOnlineProfileDialogState = {open: false, initialValues: null, onSuccess: () => void 0, onClose: () => void 0};

const ProductOnBackorderList = ({close, width, height, partNo, warehouseCode, customerId, customerName}) => {
    const [onlineUserProfileDialog, setOnlineUserProfileDialog] = useState(initNewOnlineProfileDialogState);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openingOrders, setOpeningOrders] = useState([]);
    const isMounted = useIsMounted();

    const dataGridSaveConfig = useMemo(() => {
        return ({
            name: "ProductOnBackorderListDataGrid",
            version: 2,
        });
    }, []);

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Order No",
                name: ProductOnBackorderListDataGridColumnNames.orderNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 80,
            },
            {
                title: "Created Date",
                name: ProductOnBackorderListDataGridColumnNames.createdDate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.date,
                sortable: false,
                width: 90,

            },
            {
                title: "Customer",
                name: ProductOnBackorderListDataGridColumnNames.customer,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Backorder Qty",
                name: ProductOnBackorderListDataGridColumnNames.qty,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                name: ProductOnBackorderListDataGridColumnNames.action,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                reorderable: false,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.right,
            },
        ], []);

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Fetch the purchases based on the part number and warehouse code.
     * @type {(function(CancelToken): Promise<void>)}
     */
    const getPurchases = useCallback(async (cancelToken) => {
        setLoading(true);
        let pagination = {currentPage: 1, pageSize: 100, total: 0};
        let orders = [];
        do {
            const response = await Api.searchBackOrderItems({
                filters: {
                    partNo: partNo,
                    warehouseCode: warehouseCode,
                    customerId: customerId ? [customerId] : null,
                },
                paginationInfo: pagination,
            }, cancelToken);
            if (!isMounted())
                return;
            if (response?.resultFlag) {
                orders = [...orders, ...(response.data?.resultList ?? [])];
                pagination.total = response.data?.paginationInfo?.length ?? 0;
            } else {
                break;
            }
        } while (pagination.currentPage * pagination.pageSize < pagination.total && !cancelToken?.reason);
        const groupedOrders = {};
        for (const order of orders) {
            groupedOrders[order.orderNo] = {
                orderId: order.orderId,
                orderNo: order.orderNo,
                customer: order.customer,
                createdDate: order.createdDate,
                qty: (groupedOrders[order.orderNo]?.qty ?? 0) + Number(order.backorderQty ?? "0"),
            }
        }
        const resultList = Object.values(groupedOrders);
        resultList.sort((a, b) => b.qty - a.qty);
        setOrders(resultList);
        setLoading(false);
    }, [partNo, warehouseCode, customerId, isMounted]);

    /**
     * Effect to fetch purchases when the component mounts or when partNo or warehouseCode changes.
     */
    useEffect(() => {
        const cancelTokenSource = axios.CancelToken.source();
        getPurchases(cancelTokenSource.token).then();
        return () => cancelTokenSource.cancel();
    }, [getPurchases, partNo, warehouseCode]);

    /**
     * Fetches the customer id from the given code.
     * @type {(function(*, *): Promise<void>)}
     */
    const fetchCustomerIdFromCode = useCallback(async (customer) => {
        const response = await Api.getCustomerSimpleInfoByCode(customer.customerNo);
        if (!isMounted())
            return;
        if (response.resultFlag) {
            return response.data?.id;
        } else {
            let succeeded = false;
            return await new Promise(resolve => {
                setOnlineUserProfileDialog({
                    open: true,
                    initialValues: {company: {id: customer.id, code: customer.customerNo, name: customer.name}},
                    onClose: () => {
                        if (!succeeded) {
                            resolve();
                        }
                        setOnlineUserProfileDialog(initNewOnlineProfileDialogState);
                    },
                    onSuccess: async () => {
                        succeeded = true;
                        const response = await Api.getCustomerSimpleInfoByCode(customer.customerNo);
                        resolve(response.data?.id);
                    }
                });
            })
        }
    }, [isMounted])

    /**
     * Navigates to the information view of the given customer and
     * @type {(function(*, *): Promise<void>)}
     */
    const openOrder = useCallback(async (order) => {
        setOpeningOrders((prev) => [...prev, order.orderId]);
        const customerId = await fetchCustomerIdFromCode(order.customer);
        if (!isMounted())
            return;
        setOpeningOrders((prev) => prev.filter((id) => id !== order.orderId));
        if (customerId) {
            openLinkInNewTab(window.location.origin + routeFunctions.customer.sales.backOrder.single(customerId, order.orderId));
        }
    }, [fetchCustomerIdFromCode, isMounted])

    const dataGridRows = useMemo(() => {
        return orders.map((item) => {
            return (
                /** @type DataGridRow*/
                {
                    key: item.orderNo,
                    data: item,
                    cells: {
                        [ProductOnBackorderListDataGridColumnNames.orderNo]: item.orderNo,
                        [ProductOnBackorderListDataGridColumnNames.createdDate]: item.createdDate,
                        [ProductOnBackorderListDataGridColumnNames.customer]: item.customer?.name,
                        [ProductOnBackorderListDataGridColumnNames.qty]: item.qty,
                        [ProductOnPurchaseListDataGridColumnNames.action]:
                            <Button
                                size={'small'}
                                color={'primary'}
                                disabled={openingOrders.includes(item.orderId)}
                                onClick={() => openOrder(item)}
                                startIcon={<OpenInNew/>}
                            >
                                {openingOrders.includes(item.orderId) ? "Opening..." : "Open Order"}
                            </Button>,
                    }
                }
            )
        });
    }, [openOrder, openingOrders, orders]);

    return (
        <>
            <Box
                className={'d-flex flex-column'}
                sx={{
                    width: `min(90vw, ${width}px)`,
                    maxHeight: `min(90vh, ${height}px)`,
                    minHeight: `min(90vh, ${height}px)`,
                    overflowY: 'auto',
                    position: 'relative',
                    gap: 2,
                    padding: 1,
                }}
            >
                <Box sx={{
                    position: 'sticky',
                    top: -8,
                    zIndex: 10,
                    left: 0,
                    backgroundColor: 'background.paper',
                    padding: 1,
                    paddingInlineEnd: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    borderBottom: '1px solid',
                    borderBottomColor: 'divider',
                    cursor: 'grab',
                    '&:active': {
                        cursor: 'grabbing',
                    },
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            <Tooltip title={"You can move this popover through the panel."} placement={'right'} followCursor>
                                <ControlCamera color={'primary'}/>
                            </Tooltip>
                            <Typography component={'div'} variant={'body2'} fontWeight={600} marginInlineStart={1}>
                                Backorders of {partNo ?? ""} in {warehouseCode ?? ""} {customerName ? `for ${customerName}` : ""}
                            </Typography>
                        </Box>
                        <IconButton onClick={close} color={'error'}>
                            <CloseOutlined/>
                        </IconButton>
                    </Box>
                </Box>
                <Box className={'draggable-popover-cancel'}>
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{
                            columns: columns,
                        }}
                        columns={dataGridColumns}
                        rows={dataGridRows}
                        visibleRows={6}
                        state={{
                            pagination: {currentPage: 1, pageSize: orders.length, length: orders.length},
                            loading: {state: loading},
                            density: dataGridDensity,
                        }}
                        hideFooter
                        hideExporting
                    />
                </Box>
            </Box>
            <CreateUserOnlineProfileDialog
                open={onlineUserProfileDialog?.open}
                initialValues={onlineUserProfileDialog.initialValues}
                onSuccess={onlineUserProfileDialog.onSuccess}
                setOpen={onlineUserProfileDialog.onClose}
            />
        </>
    );
}

export default ProductOnBackorderPopover;